import React from 'react'
import { useFiles } from '@/contexts/FileContext'
import type { 
  WaveChart, 
  WaveCurve,
  ChartConfig,
  StyledCurveReference
} from '../../../../interfaces/waveChart'
import type { Curve } from '@/interfaces/toolData'
import type { TimeShiftConfig } from '@/interfaces/models/TimeShift'
import { TimeShiftEngine } from '@/lib/timeshift/timeShiftEngine'

interface ChartDataResolverProps {
  /** Chart configurations with curve references */
  chartConfigs: ChartConfig[]
  
  /** Time shift configuration for applying device clock corrections */
  timeShiftConfig?: TimeShiftConfig | null
  
  /** Child component that receives resolved chart data */
  children: (resolvedCharts: WaveChart[]) => React.ReactNode
}

/**
 * Resolves chart configurations to actual chart data by pulling curves from workspace context
 * This is the bridge between lightweight ChartConfig and data-heavy WaveChart
 */
export const ChartDataResolver: React.FC<ChartDataResolverProps> = ({
  chartConfigs,
  timeShiftConfig,
  children
}) => {
  const { parsedFiles } = useFiles()
  
  // Create TimeShiftEngine instance for applying time corrections
  const timeShiftEngine = React.useMemo(() => new TimeShiftEngine(), [])
  
  /**
   * Find a curve in the parsed files by filename and curve name
   */
  const findCurve = (fileType: string, curveName: string): Curve | null => {
    // Find the parsed file by fileType
    const file = parsedFiles.find(f => f.parsedData?.fileType === fileType)
    if (!file?.parsedData) {
      console.warn(`⚠️ File not found or not parsed: ${fileType}`)
      return null
    }
    
    // Log what type of data we found in this file
    console.log(`🔍 Looking for curve "${curveName}" in ${file.name} (${fileType}):`, {
      fileType: file.parsedData.fileType,
      hasCurves: 'curves' in file.parsedData,
      curvesCount: file.parsedData.curves?.length || 0,
      hasSurveys: 'surveys' in file.parsedData,
      surveysCount: file.parsedData.surveys?.length || 0,
      availableCurves: file.parsedData.curves?.map(c => c.name) || []
    })
    
    // Find the curve within that file
    if ('curves' in file.parsedData && Array.isArray(file.parsedData.curves)) {
      const curve = file.parsedData.curves.find((c: Curve) => c.name === curveName)
      if (!curve) {
        console.warn(`⚠️ Curve not found: ${curveName} in ${file.name} (${fileType})`)
        return null
      }
      console.log(`✅ Found curve "${curveName}": ${curve.yValues.length} points`)
      return curve
    }
    
    console.warn(`⚠️ No curves found in file: ${file.name} (${fileType})`)
    return null
  }
  
  /**
   * Convert a styled curve reference to a resolved WaveCurve with data
   */
  const resolveCurveReference = (styledRef: StyledCurveReference): WaveCurve | null => {
    const ref = styledRef.reference;
    console.log(`🔄 Resolving curve reference: ${ref.fileType}:${ref.curveName}`)
    const curve = findCurve(ref.fileType, ref.curveName)
    if (!curve) return null
    
    // All xValues should be Date objects (standardized in V3)
    let xValues = curve.xValues; // Should already be Date[] from parsers
    
    const yValues = curve.yValues;
    
    // Apply time shift if configured for this device type
    if (timeShiftConfig && timeShiftConfig.shifts.size > 0) {
      console.log(`🔍 ChartDataResolver: Checking time shifts for ${ref.curveName}`, {
        availableShifts: Array.from(timeShiftConfig.shifts.keys()),
        shiftsCount: timeShiftConfig.shifts.size
      })
      
      // Find the source file to determine device type
      const sourceFile = parsedFiles.find(f => f.parsedData?.fileType === ref.fileType)
      if (sourceFile?.parsedData) {
        const deviceType = sourceFile.parsedData.toolType
        console.log(`🔍 Found device type: ${deviceType} for file: ${ref.fileType}`)
        
        // Check if there's a time shift for this device type (direct lookup by key)
        const deviceShift = timeShiftConfig.shifts.get(deviceType)
        console.log(`🔍 Device shift lookup for ${deviceType}:`, deviceShift)
        
        if (deviceShift && deviceShift.timeOffsetMs !== 0) {
          console.log(`🕒 Applying time shift to ${ref.curveName}:`, {
            deviceType,
            offsetMs: deviceShift.timeOffsetMs,
            offsetSeconds: deviceShift.timeOffsetMs / 1000
          })
          
          // Apply time shift to the curve data
          const shiftedCurve = timeShiftEngine.applyTimeShift(
            { ...curve, xValues },
            deviceShift.timeOffsetMs
          )
          xValues = shiftedCurve.xValues
        }
      }
    }
    
    // Create the WaveCurve with resolved data
    const waveCurve: WaveCurve = {
      // Use curve name as display name
      name: `${ref.fileType}: ${ref.curveName}`,
      
      // Data arrays
      xValues,
      yValues,
      
      // Units
      yUnit: curve.yUnit,
      
      // Source tracking
      sourceFile: ref.fileType,
      
      // Apply styling overrides from styled reference
      color: styledRef.styling?.color,
      strokeThickness: styledRef.styling?.strokeThickness || 2,
      lineStyle: styledRef.styling?.lineStyle || 'solid',
      yAxisId: styledRef.styling?.yAxisId || 'left',
      visible: styledRef.styling?.visible ?? true,
      opacity: styledRef.styling?.opacity ?? 1.0,
      interpolation: styledRef.styling?.interpolation || 'linear',
      
      // Point styling from styled reference
      showDataPoints: styledRef.styling?.showDataPoints ?? false,
      pointStyle: styledRef.styling?.pointStyle || 'circle',
      pointSize: styledRef.styling?.pointSize || 4,
      // New: optional display name override
      displayName: styledRef.styling?.displayName?.trim() || undefined
    }
    
    console.log(`✅ Returning WaveCurve for chart:`, {
      name: waveCurve.name,
      xLength: waveCurve.xValues.length,
      yLength: waveCurve.yValues.length,
      sourceFile: waveCurve.sourceFile,
      visible: waveCurve.visible,
      color: waveCurve.color
    })
    
    return waveCurve
  }
  
  /**
   * Resolve a chart configuration to a chart with actual data
   */
  const resolveChartConfig = (config: ChartConfig): WaveChart => {
    
    // Resolve all curve references
    const resolvedCurves: WaveCurve[] = []
    
    for (const curveRef of config.curveReferences) {
      const resolvedCurve = resolveCurveReference(curveRef)
      if (resolvedCurve) {
        resolvedCurves.push(resolvedCurve)
      }
    }
    
    if (resolvedCurves.length === 0) {
      // Don't filter out empty charts - create empty WaveChart for drop zones
    }
    
    // Create WaveChart with resolved data
    const waveChart: WaveChart = {
      id: config.id,
      title: config.title,
      curves: resolvedCurves,
      heightPreset: config.heightPreset,
      legendCorner: (config as any).legendCorner,
      legendMode: (config as any).legendMode,
      showGrid: config.showGrid,
      showCrosshairs: config.showCrosshairs,
      showTooltips: config.showTooltips,
      backgroundColor: config.backgroundColor,
      yAxes: config.yAxes
    }
    
    return waveChart
  }
  
  // Resolve all chart configurations (including empty charts)
  const resolvedCharts: WaveChart[] = chartConfigs.map(resolveChartConfig)
  
  return <>{children(resolvedCharts)}</>
}

export default ChartDataResolver 
